// src/components/multi-select.tsx
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { CheckIcon, XCircle, XIcon, WandSparkles } from "lucide-react";

import ArrowBottomIcon from "../../../assets/svgs/common/arrow-bottom.svg";

import { cn } from "../../../utils/utils";
import { Separator } from "../../../components/ui/separator";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "../../../components/ui/command";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
	"m-1 py-1 transition hover:-translate-y-1 duration-300 delay-150 ease-in-out hover:scale-110",
	{
		variants: {
			variant: {
				default:
					"border-foreground/50 text-foreground bg-card hover:bg-card/80",
				secondary:
					"border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
				destructive:
					"border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
				inverted: "inverted",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof multiSelectVariants> {
	/**
	 * An array of option objects to be displayed in the multi-select component.
	 * Each option object has a label, value, and an optional icon.
	 */
	options: {
		/** The text to display for the option. */
		label: string;
		/** The unique value associated with the option. */
		value: string;
		/** Optional icon component to display alongside the option. */
		icon?: string;
	}[];

	/**
	 * Callback function triggered when the selected values change.
	 * Receives an array of the new selected values.
	 */
	onValueChange: (value: string[]) => void;

	/** The default selected values when the component mounts. */
	defaultValue?: string[];

	/**
	 * Placeholder text to be displayed when no values are selected.
	 * Optional, defaults to "Select options".
	 */
	placeholder?: string;

	/**
	 * Animation duration in seconds for the visual effects (e.g., bouncing badges).
	 * Optional, defaults to 0 (no animation).
	 */
	animation?: number;

	/**
	 * Maximum number of items to display. Extra selected items will be summarized.
	 * Optional, defaults to 3.
	 */
	maxCount?: number;

	/**
	 * The modality of the popover. When set to true, interaction with outside elements
	 * will be disabled and only popover content will be visible to screen readers.
	 * Optional, defaults to false.
	 */
	modalPopover?: boolean;

	/**
	 * If true, renders the multi-select component as a child of another component.
	 * Optional, defaults to false.
	 */
	asChild?: boolean;

	/**
	 * Additional class names to apply custom styles to the multi-select component.
	 * Optional, can be used to add custom styles.
	 */
	className?: string;
}

export const MultiSelect = React.forwardRef<
	HTMLButtonElement,
	MultiSelectProps
>(
	(
		{
			options,
			onValueChange,
			variant,
			defaultValue = [],
			placeholder = "Select options",
			animation = 0,
			maxCount = 3,
			modalPopover = false,
			//   asChild = false,
			className,
			...props
		},
		ref,
	) => {
		const [selectedValues, setSelectedValues] =
			React.useState<string[]>(defaultValue);
		const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
		const [isAnimating, setIsAnimating] = React.useState(false);

		const handleInputKeyDown = (
			event: React.KeyboardEvent<HTMLInputElement>,
		) => {
			if (event.key === "Enter") {
				setIsPopoverOpen(true);
			} else if (event.key === "Backspace" && !event.currentTarget.value) {
				const newSelectedValues = [...selectedValues];
				newSelectedValues.pop();
				setSelectedValues(newSelectedValues);
				onValueChange(newSelectedValues);
			}
		};

		const toggleOption = (option: string) => {
			const newSelectedValues = selectedValues.includes(option)
				? selectedValues.filter((value) => value !== option)
				: [...selectedValues, option];
			setSelectedValues(newSelectedValues);
			onValueChange(newSelectedValues);
		};

		const handleClear = () => {
			setSelectedValues([]);
			onValueChange([]);
		};

		const handleTogglePopover = () => {
			setIsPopoverOpen((prev) => !prev);
		};

		const clearExtraOptions = () => {
			const newSelectedValues = selectedValues.slice(0, maxCount);
			setSelectedValues(newSelectedValues);
			onValueChange(newSelectedValues);
		};

		const toggleAll = () => {
			if (selectedValues.length === options.length) {
				handleClear();
			} else {
				const allValues = options.map((option) => option.value);
				setSelectedValues(allValues);
				onValueChange(allValues);
			}
		};

		return (
			<Popover
				open={isPopoverOpen}
				onOpenChange={setIsPopoverOpen}
				modal={modalPopover}
			>
				<PopoverTrigger asChild>
					<Button
						ref={ref}
						{...props}
						onClick={handleTogglePopover}
						className={cn(
							"flex justify-between items-center bg-inherit hover:bg-inherit p-1 border rounded-md w-full h-auto min-h-10 [&_svg]:pointer-events-auto",
							className,
						)}
					>
						{selectedValues.length > 0 ? (
							<div className="flex justify-between items-center w-full">
								<div className="flex items-center overflow-hidden">
									{selectedValues.slice(0, maxCount).map((value) => {
										const option = options.find((o) => o.value === value);
										return (
											<Badge
												key={value}
												className={cn(
													isAnimating ? "animate-bounce" : "",
													multiSelectVariants({ variant }),
												)}
												style={{ animationDuration: `${animation}s` }}
											>
												{option?.icon} {" "}
												{option?.label}
												<XCircle
													className="ml-2 w-4 h-4 cursor-pointer"
													onClick={(event) => {
														event.stopPropagation();
														toggleOption(value);
													}}
												/>
											</Badge>
										);
									})}
									{selectedValues.length > maxCount && (
										<Badge
											className={cn(
												"bg-transparent hover:bg-transparent border-foreground/1 text-foreground",
												isAnimating ? "animate-bounce" : "",
												multiSelectVariants({ variant }),
											)}
											style={{ animationDuration: `${animation}s` }}
										>
											{`+ ${selectedValues.length - maxCount} more`}
											<XCircle
												className="ml-2 w-4 h-4 cursor-pointer"
												onClick={(event) => {
													event.stopPropagation();
													clearExtraOptions();
												}}
											/>
										</Badge>
									)}
								</div>
								<div className="flex justify-between items-center">
									<XIcon
										className="mx-2 h-4 text-secondary cursor-pointer"
										onClick={(event) => {
											event.stopPropagation();
											handleClear();
										}}
									/>
									<Separator
										orientation="vertical"
										className="flex h-full min-h-6"
									/>
									<div className="px-3">
										<ArrowBottomIcon />
									</div>
								</div>
							</div>
						) : (
							<div className="flex justify-between items-center mx-auto w-full">
								<span className="mx-3 text-secondary text-sm">
									{placeholder}
								</span>
								<div className="px-3">
									<ArrowBottomIcon />
								</div>
							</div>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="bg-white p-0 border border-black w-full font-cabinetgrotesk font-semibold text-black"
					align="start"
					onEscapeKeyDown={() => setIsPopoverOpen(false)}
				>
					<Command className="bg-white text-black">
						<CommandInput
							className="placeholder:text-secondary"
							placeholder="Search..."
							onKeyDown={handleInputKeyDown}
						/>
				
						<CommandList>
	
								<CommandList>
									<CommandEmpty>No results found.</CommandEmpty>
									<CommandGroup>
										<CommandItem
											key="all"
											onSelect={toggleAll}
											className={cn(
												"hover:text-black cursor-pointer",
												selectedValues.length === options.length
													? "text-black"
													: "text-black",
											)}
										>
											<div
												className={cn(
													"flex justify-center items-center mr-2 border border-black rounded-sm w-4 h-4 text-white",
													selectedValues.length === options.length
														? "bg-secondary text-black"
														: "opacity-50 [&_svg]:invisible",
												)}
											>
												<CheckIcon className="w-4 h-4 text-white" />
											</div>
											(Select All)
										</CommandItem>
										{options.map((option) => {
											const isSelected = selectedValues.some(
												(value) => value === option.value,
											);
											return (
												<CommandItem
													key={option.value}
													onSelect={() =>
														toggleOption(option.value)
														
													}
													className={cn(
														"hover:text-black cursor-pointer",
														isSelected ? "text-black" : "text-black",
													)}
												>
													<div
														className={cn(
															"flex justify-center items-center mr-2 border border-black rounded-sm w-4 h-4",
															isSelected
																? "bg-secondary text-black"
																: "opacity-50 [&_svg]:invisible",
														)}
													>
														<CheckIcon className="w-4 h-4 text-white" />
													</div>
													{option.icon && (
														<span className="mr-2">{option.icon}</span>
													)}

													{option.label}
												</CommandItem>
											);
										})}
									</CommandGroup>
									<CommandSeparator className="bg-black" />
									<CommandGroup>
										<div className="flex justify-between items-center">
											{selectedValues.length > 0 && (
												<>
													<CommandItem
														onSelect={handleClear}
														className="flex-1 justify-center border border-black text-black cursor-pointer"
													>
														Clear
													</CommandItem>
													<Separator
														orientation="vertical"
														className="flex h-full min-h-6"
													/>
												</>
											)}
											<CommandItem
												onSelect={() => setIsPopoverOpen(false)}
												className="flex-1 justify-center bg-primarybuttonbg hover:bg-primarybuttonbg border border-black max-w-full text-black cursor-pointer"
											>
												Close
											</CommandItem>
										</div>
									</CommandGroup>
								</CommandList>
						</CommandList>
					</Command>
				</PopoverContent>
				{animation > 0 && selectedValues.length > 0 && (
					<WandSparkles
						className={cn(
							"bg-background my-2 w-3 h-3 text-foreground cursor-pointer",
							isAnimating ? "" : "text-muted-foreground",
						)}
						onClick={() => setIsAnimating(!isAnimating)}
					/>
				)}
			</Popover>
		);
	},
);

MultiSelect.displayName = "MultiSelect";
